import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "sonner";
import { formatPhone, telHref } from "../../lib/phone";
import {
  PhoneCall, Lightning, PencilSimple, CalendarPlus, HandGrabbing, Tray,
  ClockCountdown, CheckCircle, UserPlus, Timer, Target, Trophy, CaretDown,
  CaretUp, Users, CalendarCheck, Archive, ArrowsClockwise,
} from "@phosphor-icons/react";
import { StatTile, Panel, StatusPill, EmptyState, pct1 } from "../lux";
import { humanMinutes } from "../PeriodPicker";
import { StatusCell, LeadSourceIcon } from "./LeadCells";
import { LeadDetailsButton } from "./LeadDetails";

const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const inDays = (days, hour = 9) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return toLocalInput(d);
};
const fmtDay = (s) =>
  s ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00` : s).toLocaleString(undefined,
    { month: "short", day: "numeric" }) : "—";
const fmtDayTime = (s) =>
  s ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00` : s).toLocaleString(undefined,
    { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

// Age since the lead landed, and the SLA colour that goes with it.
const ageMinutes = (iso) => (iso ? Math.max((Date.now() - new Date(iso)) / 60000, 0) : null);
export const slaTone = (iso) => {
  const m = ageMinutes(iso);
  if (m === null) return "neutral";
  return m >= 1440 ? "red" : m >= 240 ? "amber" : "green";
};
const FIG_TONE = { red: "urg-red", amber: "urg-amber", green: "urg-green", neutral: "text-muted-foreground" };
const dueTone = (iso) => {
  if (!iso) return "neutral";
  const day = String(iso).slice(0, 10);
  const today = new Date();
  const t = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  return day < t ? "red" : day === t ? "amber" : "neutral";
};

function FollowUpButton({ lead, onSet, busy }) {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(lead.next_follow_up_at ? String(lead.next_follow_up_at).slice(0, 16) : "");
  const apply = async (value) => {
    await onSet(value);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm" variant="outline" disabled={busy}
          className={`w-[136px] justify-start ${lead.next_follow_up_at ? "border-primary/50 text-primary" : ""}`}
          data-testid={`lead-followup-btn-${lead.id}`}
        >
          <CalendarPlus size={13} className="mr-1 shrink-0" />
          <span className="truncate">{lead.next_follow_up_at ? fmtDayTime(lead.next_follow_up_at) : "Follow up"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 bg-card border-border" align="end" data-testid={`lead-followup-popover-${lead.id}`}>
        <div className="label-mono mb-3">Next follow-up</div>
        <div className="flex flex-wrap gap-2 mb-4">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => apply(inDays(1))} data-testid={`lead-followup-tomorrow-${lead.id}`}>Tomorrow</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => apply(inDays(3))} data-testid={`lead-followup-3d-${lead.id}`}>+3 days</Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => apply(inDays(7))} data-testid={`lead-followup-week-${lead.id}`}>Next week</Button>
        </div>
        <Input
          type="datetime-local" step={900} value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="bg-input border-border mb-3"
          data-testid={`lead-followup-input-${lead.id}`}
        />
        <div className="flex items-center justify-between gap-2">
          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => apply("")} data-testid={`lead-followup-clear-${lead.id}`}>Clear</Button>
          <Button size="sm" disabled={!when} onClick={() => apply(when)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`lead-followup-save-${lead.id}`}>Set</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LeadQueueRow({ lead, figure, figureLabel, tone = "neutral", actions, onRefresh, testid, mark, onEdit }) {
  // SALES-031 follow-up B1: one shared grid for EVERY My Leads section so the
  // name / figure / status / actions start at the same x on every row. The
  // actions column is a fixed width sized for the widest set any section shows,
  // left-aligned so the first action lines up everywhere. Stacks at <md.
  const details = `${lead.lead_no ? `#${lead.lead_no} · ` : ""}${lead.service_type || "rental"}${lead.source ? ` · ${lead.source}` : ""}${lead.customer_phone ? ` · ${formatPhone(lead.customer_phone)}` : ""}`;
  const phoneHref = telHref(lead.customer_phone);
  return (
    <div
      className="px-5 py-4 grid grid-cols-1 gap-x-5 gap-y-3 items-center hover:bg-white/[0.02] transition-colors duration-150 md:grid-cols-[minmax(0,1fr)_9rem_9.5rem_21rem]"
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="font-heading text-base truncate" title={lead.customer_name || "Unnamed lead"}>{lead.customer_name || "Unnamed lead"}</div>
        <div className="text-xs text-muted-foreground mt-0.5 truncate" title={details}>
          <LeadSourceIcon lead={lead} testid={testid ? `${testid}-channel` : undefined} />
          {lead.lead_no ? <span className="tnum">{`#${lead.lead_no} · `}</span> : null}
          <span className="capitalize">{lead.service_type || "rental"}</span>
          {lead.source ? ` · ${lead.source}` : ""}
          {lead.customer_phone ? (phoneHref
            ? <> · <a href={phoneHref} className="text-primary hover:underline" data-testid={`myleads-phone-${lead.id}`}>{formatPhone(lead.customer_phone)}</a></>
            : ` · ${formatPhone(lead.customer_phone)}`) : ""}
        </div>
        <LeadDetailsButton lead={lead} onEdit={onEdit} className="mt-1" testid={testid ? `${testid}-details` : undefined} />
        {mark && <div className="text-[11px] text-emerald-400 mt-1" data-testid={testid ? `${testid}-mark` : undefined}>{mark}</div>}
      </div>
      <div className="min-w-0" data-testid={testid ? `${testid}-figure-col` : undefined}>
        <div className="label-mono">{figureLabel}</div>
        <div className={`font-heading text-lg tnum leading-tight whitespace-nowrap ${FIG_TONE[tone]}`} data-testid={testid ? `${testid}-figure` : undefined}>
          {figure}
        </div>
      </div>
      <div className="min-w-0" data-testid={testid ? `${testid}-status-col` : undefined}>
        {onRefresh ? <StatusCell lead={lead} onSaved={onRefresh} /> : <StatusPill status={lead.status || "new"} />}
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-start md:flex-nowrap" data-testid={testid ? `${testid}-actions` : undefined}>{actions}</div>
    </div>
  );
}

function Section({ title, label, icon, count, children, testid, actions }) {
  return (
    <Panel label={label} title={`${title}${count ? ` · ${count}` : ""}`} icon={icon} testid={testid} flush actions={actions}>
      {children}
    </Panel>
  );
}

export function MyLeadsPanel({ onEdit, pins, setPins }) {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const [showPool, setShowPool] = useState(false);

  const queue = useQuery({
    queryKey: ["leads-mine"],
    queryFn: async () => (await api.get("/leads/mine")).data,
  });
  const pool = useQuery({
    queryKey: ["leads-unassigned"],
    queryFn: async () => (await api.get("/leads/unassigned")).data,
  });

  // A plain refetch — counts update from the server, but pinned rows stay put.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["leads-mine"] });
    qc.invalidateQueries({ queryKey: ["leads-unassigned"] });
    qc.invalidateQueries({ queryKey: ["notifications-feed"] });
  };
  // The only thing that lets an actioned lead re-bucket: an explicit Refresh.
  const manualRefresh = () => { setPins({}); refresh(); };

  // SALES-031: after an action, the lead stays in the section the rep clicked it
  // (marked) for this visit. Counts come straight from the server (shared query),
  // so the tab badge, the tiles and the section counts always agree; only WHERE
  // the row is shown is held steady, until Refresh or reload.
  const act = async (id, fn, msg, section, mark) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(msg);
      if (section) setPins((p) => ({ ...p, [id]: { section, mark } }));
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const q = queue.data;
  const s = q?.stats || {};

  const allById = useMemo(() => {
    const m = {};
    for (const arr of [q?.needs_reply, q?.follow_up_due, q?.in_progress, q?.closed, pool.data?.rows]) {
      for (const r of (arr || [])) m[r.id] = r;
    }
    return m;
  }, [q, pool.data]);

  // Server rows for a section, with the pin overlay applied: pinned-here leads
  // stay (even if the refetch moved them out); leads pinned elsewhere are removed.
  const displayFor = (serverRows, sectionKey) => {
    const kept = (serverRows || []).filter((r) => !pins[r.id] || pins[r.id].section === sectionKey);
    const keptIds = new Set(kept.map((r) => r.id));
    const inject = Object.keys(pins)
      .filter((id) => pins[id].section === sectionKey && !keptIds.has(id) && allById[id])
      .map((id) => allById[id]);
    return [...inject, ...kept];
  };

  if (queue.isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="my-leads-loading">
        {[...Array(5)].map((_, i) => <div key={i} className="h-36 bg-card border border-border animate-pulse" />)}
      </div>
    );
  }
  if (queue.isError) return <div className="text-destructive" data-testid="my-leads-error">Unable to load your lead queue.</div>;

  const followUpAction = (l, section) => (
    <FollowUpButton
      key="fu" lead={l} busy={busyId === l.id}
      onSet={(v) => act(l.id, () => api.post(`/leads/${l.id}/follow-up`, { next_follow_up_at: v }), v ? "Follow-up set" : "Follow-up cleared", section, v ? "Follow-up set" : undefined)}
    />
  );
  const convertAction = (l, section) => (
    <Button key="convert" size="sm" variant="outline" disabled={busyId === l.id}
      onClick={() => act(l.id, () => api.post(`/leads/${l.id}/convert`), "Converted — the booking is now in Rental ▸ Pre-bookings", section, "Converted ✓ — moves to Closed on refresh")}
      data-testid={`myleads-convert-${l.id}`}>
      <Lightning size={13} className="mr-1 text-primary" />Convert
    </Button>
  );
  const editAction = (l) => (
    <Button key="edit" size="sm" variant="ghost" onClick={() => onEdit(l)} data-testid={`myleads-edit-${l.id}`}><PencilSimple size={14} /></Button>
  );

  // A reply-stage lead: Contacted, unless it has already been actioned this visit.
  const replyActions = (l) => [
    <Button
      key="contact" size="sm" variant="outline" disabled={busyId === l.id}
      onClick={() => act(l.id, () => api.post(`/leads/${l.id}/contact`), "Marked contacted", "needs_reply", "Contacted ✓ — moves to In progress on refresh")}
      className="border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
      data-testid={`myleads-contact-${l.id}`}
    >
      <PhoneCall size={13} className="mr-1" />Contacted ✓
    </Button>,
    followUpAction(l, "needs_reply"),
    editAction(l),
  ];
  const workActions = (l, section) => [followUpAction(l, section), convertAction(l, section), editAction(l)];

  return (
    <div className="space-y-6" data-testid="my-leads-panel">
      <div className="flex items-center justify-end">
        <Button size="sm" variant="outline" className="rounded-full" onClick={manualRefresh} data-testid="myleads-refresh">
          <ArrowsClockwise size={14} className="mr-1.5" />Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 stagger" data-testid="my-leads-stats">
        <StatTile label="My Open Leads" value={s.open_leads ?? 0} hint={q?.me ? `Owned by ${q.me}` : "Nothing assigned to you yet"} icon={UserPlus} tone="blue" testid="myleads-stat-open" />
        <StatTile
          label="Needs My Reply" value={s.needs_reply ?? 0}
          hint={s.over_24h ? `${s.over_24h} waiting over 24h`
            : s.over_4h ? `${s.over_4h} waiting over 4h`
              : s.needs_reply ? "All under 4h" : "All fresh"}
          icon={Tray} tone={s.over_24h ? "red" : s.over_4h ? "amber" : s.needs_reply ? "blue" : "neutral"} testid="myleads-stat-reply"
        />
        <StatTile label="My Avg Response" value={humanMinutes(s.avg_response_minutes)} hint="First reply after a lead lands" icon={Timer} tone="neutral" testid="myleads-stat-response" />
        <StatTile label="Won This Month" value={s.won_this_month ?? 0} hint={`${s.closed_this_month ?? 0} closed this month`} icon={Trophy} tone="green" testid="myleads-stat-won" />
        <StatTile label="Lead close" value={s.lead_close && s.lead_close.of ? pct1(s.lead_close.rate) : "—"} hint={s.lead_close && s.lead_close.of ? `${s.lead_close.won} of ${s.lead_close.of} leads` : "No leads given this month"} icon={Target} tone="gold" gold testid="myleads-stat-close" />
      </div>

      <Section label="Reply now" title="Needs reply" icon={Tray} testid="myleads-needs-reply" count={(q?.needs_reply || []).length}>
        {displayFor(q?.needs_reply, "needs_reply").length === 0 ? (
          <EmptyState icon={CheckCircle} title="Nothing waiting on you" hint="Every lead you own has had a first reply." testid="myleads-needs-reply-empty" />
        ) : (
          <div className="divide-y divide-border/50">
            {displayFor(q?.needs_reply, "needs_reply").map((l) => (
              <LeadQueueRow onEdit={onEdit}
                key={l.id} lead={l} testid={`myleads-reply-row-${l.id}`}
                figureLabel="Waiting" figure={humanMinutes(ageMinutes(l.created_at))} tone={slaTone(l.created_at)}
                actions={pins[l.id] ? workActions(l, "needs_reply") : replyActions(l)} mark={pins[l.id]?.mark} onRefresh={refresh}
              />
            ))}
          </div>
        )}
      </Section>

      <Section label="Due" title="Follow-up due" icon={ClockCountdown} testid="myleads-followups" count={(q?.follow_up_due || []).length}>
        {displayFor(q?.follow_up_due, "follow_up_due").length === 0 ? (
          <EmptyState icon={CalendarCheck} title="No follow-ups due" hint="Schedule one from any lead so it can't slip." testid="myleads-followups-empty" />
        ) : (
          <div className="divide-y divide-border/50">
            {displayFor(q?.follow_up_due, "follow_up_due").map((l) => (
              <LeadQueueRow onEdit={onEdit}
                key={l.id} lead={l} testid={`myleads-followup-row-${l.id}`}
                figureLabel="Due" figure={fmtDay(l.next_follow_up_at)} tone={dueTone(l.next_follow_up_at)}
                actions={workActions(l, "follow_up_due")} mark={pins[l.id]?.mark} onRefresh={refresh}
              />
            ))}
          </div>
        )}
      </Section>

      <Section label="Live" title="In progress" icon={Users} testid="myleads-in-progress" count={(q?.in_progress || []).length}>
        {displayFor(q?.in_progress, "in_progress").length === 0 ? (
          <EmptyState icon={Users} title="No live conversations" hint="Leads you've replied to appear here until they close." testid="myleads-in-progress-empty" />
        ) : (
          <div className="divide-y divide-border/50">
            {displayFor(q?.in_progress, "in_progress").map((l) => (
              <LeadQueueRow onEdit={onEdit}
                key={l.id} lead={l} testid={`myleads-progress-row-${l.id}`}
                figureLabel="Last touch" figure={humanMinutes(ageMinutes(l.last_activity_at || l.updated_at))} tone="neutral"
                actions={workActions(l, "in_progress")} mark={pins[l.id]?.mark} onRefresh={refresh}
              />
            ))}
          </div>
        )}
      </Section>

      <Section
        label="Pool" title="Unassigned leads" icon={HandGrabbing} testid="myleads-pool"
        count={pool.data?.count || 0}
        actions={
          <Button size="sm" variant="ghost" onClick={() => setShowPool((v) => !v)} data-testid="myleads-pool-toggle">
            {showPool ? <>Hide <CaretUp size={13} className="ml-1" /></> : <>Show <CaretDown size={13} className="ml-1" /></>}
          </Button>
        }
      >
        {!showPool ? (
          <div className="px-5 py-4 text-sm text-muted-foreground" data-testid="myleads-pool-collapsed">
            {pool.data?.count || 0} open lead{(pool.data?.count || 0) === 1 ? "" : "s"} nobody owns yet — claim any of them to add it to your queue.
          </div>
        ) : displayFor(pool.data?.rows, "pool").length === 0 ? (
          <EmptyState icon={CheckCircle} title="The pool is empty" hint="Every open lead has an owner." testid="myleads-pool-empty" />
        ) : (
          <>
            <div className="divide-y divide-border/50">
              {displayFor(pool.data?.rows, "pool").map((l) => (
                <LeadQueueRow onEdit={pins[l.id] ? onEdit : undefined}
                  key={l.id} lead={l} testid={`myleads-pool-row-${l.id}`}
                  figureLabel="Waiting" figure={humanMinutes(ageMinutes(l.created_at))} tone={slaTone(l.created_at)}
                  mark={pins[l.id]?.mark}
                  actions={pins[l.id] ? workActions(l, "pool") : [
                    <Button
                      key="claim" size="sm" disabled={busyId === l.id}
                      onClick={() => act(l.id, () => api.post(`/leads/${l.id}/claim`), "Lead claimed — it's yours", "pool", "Claimed ✓ — moves to your queue on refresh")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid={`myleads-claim-${l.id}`}
                    >
                      <HandGrabbing size={13} className="mr-1" />Claim
                    </Button>,
                    <Button
                      key="contact" size="sm" variant="outline" disabled={busyId === l.id}
                      onClick={() => act(l.id, () => api.post(`/leads/${l.id}/contact`), "Claimed & marked contacted", "pool", "Contacted ✓ — moves to your queue on refresh")}
                      className="border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10"
                      data-testid={`myleads-pool-contact-${l.id}`}
                    >
                      <PhoneCall size={13} className="mr-1" />Contacted ✓
                    </Button>,
                    <Button key="edit" size="sm" variant="ghost" onClick={() => onEdit(l)} data-testid={`myleads-pool-edit-${l.id}`}><PencilSimple size={14} /></Button>,
                  ]}
                />
              ))}
            </div>
            {pool.data.count > pool.data.shown && (
              <div className="px-5 py-3 text-xs text-muted-foreground border-t border-border" data-testid="myleads-pool-more">
                Showing the {pool.data.shown} newest of {pool.data.count} unassigned leads.
              </div>
            )}
          </>
        )}
      </Section>

      <Section
        label="Last 30 days" title="Closed" icon={Archive} testid="myleads-closed" count={(q?.closed || []).length}
        actions={
          <Button size="sm" variant="ghost" onClick={() => setShowClosed((v) => !v)} data-testid="myleads-closed-toggle">
            {showClosed ? <>Hide <CaretUp size={13} className="ml-1" /></> : <>Show <CaretDown size={13} className="ml-1" /></>}
          </Button>
        }
      >
        {!showClosed ? (
          <div className="px-5 py-4 text-sm text-muted-foreground" data-testid="myleads-closed-collapsed">
            {(q?.closed || []).length} lead{(q?.closed || []).length === 1 ? "" : "s"} you won or lost in the last 30 days.
          </div>
        ) : (q?.closed || []).length === 0 ? (
          <EmptyState icon={Archive} title="Nothing closed yet" testid="myleads-closed-empty" />
        ) : (
          <div className="divide-y divide-border/50">
            {q.closed.map((l) => (
              <LeadQueueRow onEdit={onEdit}
                key={l.id} lead={l} testid={`myleads-closed-row-${l.id}`}
                figureLabel={(l.status || "").toLowerCase() === "won" ? "Won" : "Lost"}
                figure={fmtDay(l.converted_at || l.updated_at)}
                tone={(l.status || "").toLowerCase() === "won" ? "green" : "neutral"}
                actions={[
                  <span key="reason" className="text-xs text-muted-foreground">{l.lost_reason || ""}</span>,
                  <Button key="edit" size="sm" variant="ghost" onClick={() => onEdit(l)} data-testid={`myleads-closed-edit-${l.id}`}><PencilSimple size={14} /></Button>,
                ]}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
